import React, { useState, useEffect, useCallback } from 'react';
import axios from './lib/axiosClient';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const TIME_OPTIONS = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += 30) {
    TIME_OPTIONS.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
  }
}

function formatTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

// ============================================================================
// ADMIN WRAPPER — Tenant picker + SiteSettings in admin mode
// ============================================================================
export function AdminSiteManager() {
  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetchTenants = async () => {
      try {
        const res = await axios.get('/api/site/admin/list');
        setTenants(res.data.tenants || []);
      } catch (err) {
        console.error('Failed to load tenants:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchTenants();
  }, []);

  const filtered = tenants.filter(t => {
    const matchSearch = (t.business_name || '').toLowerCase().includes(search.toLowerCase()) ||
      (t.site?.custom_slug || '').toLowerCase().includes(search.toLowerCase());
    if (filter === 'live') return matchSearch && t.site?.is_published;
    if (filter === 'draft') return matchSearch && !t.site?.is_published;
    return matchSearch;
  });
  const liveCount = tenants.filter(t => t.site?.is_published).length;
  const draftCount = tenants.filter(t => !t.site?.is_published).length;

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading tenants...</div>;

  return (
    <div style={{ minHeight: '100vh' }}>
      <div style={{ maxWidth: 920, margin: '0 auto', padding: '24px 16px' }}>
        <div style={{ background: '#1e293b', borderRadius: 16, padding: 24, border: '1px solid #334155', marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
            <div>
              <h2 style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 700, color: '#f1f5f9' }}>🌐 Client Websites</h2>
              <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>{tenants.length} sites · {liveCount} live · {draftCount} draft</p>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {[{ k: 'all', l: 'All (' + tenants.length + ')' }, { k: 'live', l: '● Live (' + liveCount + ')' }, { k: 'draft', l: '○ Draft (' + draftCount + ')' }].map(f => (
                <button key={f.k} onClick={() => setFilter(f.k)} style={{
                  padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  background: filter === f.k ? '#a78bfa' : '#0f172a', color: filter === f.k ? '#fff' : '#94a3b8',
                  border: filter === f.k ? 'none' : '1px solid #334155',
                }}>{f.l}</button>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#0f172a', borderRadius: 10, padding: '10px 14px', border: '1px solid #334155', marginBottom: 16 }}>
            <span style={{ color: '#64748b', fontSize: 16 }}>🔍</span>
            <input placeholder="Search businesses..." value={search} onChange={e => setSearch(e.target.value)}
              style={{ background: 'transparent', border: 'none', color: '#f1f5f9', fontSize: 14, outline: 'none', width: '100%' }} />
            {search && <span onClick={() => setSearch('')} style={{ color: '#64748b', cursor: 'pointer', fontSize: 14 }}>✕</span>}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))', gap: 12 }}>
            {filtered.map(t => {
              const isSelected = t.id === selectedTenantId;
              const isLive = t.site?.is_published;
              return (
                <div key={t.id} onClick={() => setSelectedTenantId(isSelected ? null : t.id)}
                  style={{
                    padding: '16px', borderRadius: 12, cursor: 'pointer',
                    border: isSelected ? '2px solid #a78bfa' : '1px solid #334155',
                    background: isSelected ? '#a78bfa12' : '#0f172a',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { if (!isSelected) e.currentTarget.style.borderColor = '#475569'; }}
                  onMouseLeave={e => { if (!isSelected) e.currentTarget.style.borderColor = '#334155'; }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                    <div style={{ fontWeight: 700, fontSize: 15, color: '#f1f5f9' }}>{t.business_name}</div>
                    <span style={{
                      padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                      background: isLive ? '#22c55e20' : '#f59e0b20',
                      color: isLive ? '#4ade80' : '#fbbf24',
                    }}>{isLive ? '● Live' : '○ Draft'}</span>
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>{t.business_type || 'other'} · {t.plan}</div>
                  {t.site?.custom_slug && (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <a href={'/site/' + t.site.custom_slug} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                        style={{ fontSize: 11, color: '#60a5fa', textDecoration: 'none', padding: '4px 10px', background: '#3b82f615', borderRadius: 6, border: '1px solid #3b82f630' }}>🌐 View Site</a>
                      <a href={'/book/' + (t.subdomain || t.site.custom_slug)} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                        style={{ fontSize: 11, color: '#fb923c', textDecoration: 'none', padding: '4px 10px', background: '#f9731615', borderRadius: 6, border: '1px solid #f9731630' }}>📅 Booking</a>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {filtered.length === 0 && (
            <p style={{ color: '#64748b', textAlign: 'center', padding: 20, fontSize: 14 }}>No sites match your search</p>
          )}
        </div>
        {selectedTenantId && (
          <SiteSettings adminTenantId={selectedTenantId} key={selectedTenantId} />
        )}
      </div>
    </div>
  );
}

// ============================================================================
// IMAGE UPLOADER — Drag & drop or click, compresses to base64
// ============================================================================
function ImageUploader({ value, onChange, label, maxWidth = 1200, maxHeight = 800, maxSizeKB = 400 }) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = React.useRef();

  const processFile = async (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    setUploading(true);
    try {
      const dataUrl = await compressImage(file, maxWidth, maxHeight, maxSizeKB);
      onChange(dataUrl);
    } catch (err) { console.error('Upload error:', err); }
    setUploading(false);
  };

  const handleDrop = (e) => { e.preventDefault(); setDragging(false); processFile(e.dataTransfer.files[0]); };
  const handleDragOver = (e) => { e.preventDefault(); setDragging(true); };
  const handleDragLeave = () => setDragging(false);

  return (
    <div>
      {value && (
        <div style={{ marginBottom: 12, position: 'relative', display: 'inline-block' }}>
          <img src={value} alt="Preview" style={{ maxWidth: 280, maxHeight: 180, borderRadius: 10, border: '1px solid #e2e8f0', objectFit: 'contain', background: '#f8fafc' }} />
          <button onClick={() => onChange('')} style={{
            position: 'absolute', top: 6, right: 6, width: 26, height: 26, borderRadius: '50%',
            background: '#ef4444', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 700, lineHeight: '26px',
          }}>×</button>
        </div>
      )}
      <div
        onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave}
        onClick={() => fileRef.current?.click()}
        style={{
          border: dragging ? '2px solid #2563eb' : '2px dashed #cbd5e1',
          borderRadius: 12, padding: '28px 20px', textAlign: 'center', cursor: 'pointer',
          background: dragging ? '#eff6ff' : '#f8fafc', transition: 'all 0.15s',
        }}
      >
        <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
          onChange={e => processFile(e.target.files[0])} />
        {uploading ? (
          <div style={{ color: '#2563eb', fontWeight: 600, fontSize: 14 }}>Processing...</div>
        ) : (
          <>
            <div style={{ fontSize: 28, marginBottom: 6 }}>📷</div>
            <div style={{ fontWeight: 600, fontSize: 14, color: '#334155' }}>{label || 'Upload Image'}</div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>Drag & drop or click to select • JPG, PNG, WebP</div>
          </>
        )}
      </div>
      {!value && (
        <div style={{ marginTop: 8 }}>
          <input
            style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, color: '#334155', background: '#fff' }}
            placeholder="Or paste an image URL..."
            onBlur={e => { if (e.target.value.startsWith('http')) onChange(e.target.value); }}
            onKeyDown={e => { if (e.key === 'Enter' && e.target.value.startsWith('http')) onChange(e.target.value); }}
          />
        </div>
      )}
    </div>
  );
}

function GalleryUploader({ images, onChange }) {
  const fileRef = React.useRef();
  const [uploading, setUploading] = useState(false);

  const addImage = async (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    setUploading(true);
    try {
      const dataUrl = await compressImage(file, 1000, 800, 350);
      onChange([...(images || []), dataUrl]);
    } catch (err) { console.error(err); }
    setUploading(false);
  };

  const removeImage = (idx) => { onChange(images.filter((_, i) => i !== idx)); };

  return (
    <div>
      {images && images.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10, marginBottom: 14 }}>
          {images.map((url, i) => (
            <div key={i} style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', border: '1px solid #e2e8f0', aspectRatio: '4/3' }}>
              <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              <button onClick={() => removeImage(i)} style={{
                position: 'absolute', top: 4, right: 4, width: 22, height: 22, borderRadius: '50%',
                background: '#ef4444', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700, lineHeight: '22px',
              }}>×</button>
            </div>
          ))}
        </div>
      )}
      <button onClick={() => fileRef.current?.click()} disabled={uploading || (images || []).length >= 12}
        style={{
          padding: '10px 20px', border: '2px dashed #cbd5e1', borderRadius: 10,
          background: '#f8fafc', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#475569',
        }}>
        {uploading ? 'Processing...' : `📷 Add Photo (${(images || []).length}/12)`}
      </button>
      <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
        onChange={e => addImage(e.target.files[0])} />
    </div>
  );
}

async function compressImage(file, maxW, maxH, maxKB) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let w = img.width, h = img.height;
        if (w > maxW) { h = h * maxW / w; w = maxW; }
        if (h > maxH) { w = w * maxH / h; h = maxH; }
        canvas.width = Math.round(w);
        canvas.height = Math.round(h);
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        let quality = 0.85;
        let result = canvas.toDataURL('image/jpeg', quality);
        // Compress more if needed
        while (result.length > maxKB * 1370 && quality > 0.3) {
          quality -= 0.1;
          result = canvas.toDataURL('image/jpeg', quality);
        }
        resolve(result);
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// ============================================================================
// SITE SETTINGS EDITOR
// ============================================================================
export default function SiteSettings({ adminTenantId }) {
  const isAdmin = !!adminTenantId;
  const [settings, setSettings] = useState(null);
  const [tenantInfo, setTenantInfo] = useState(null);
  const [domainEnabled, setDomainEnabled] = useState(false);
  const [domainVerifying, setDomainVerifying] = useState(false);
  const [domainStatus, setDomainStatus] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [activeTab, setActiveTab] = useState('general');
  const [newTestimonial, setNewTestimonial] = useState({ author_name: '', content: '', rating: 5 });

  const basePath = isAdmin ? `/api/site/admin/${adminTenantId}` : '/api/site';
  const testimonialsPath = isAdmin
    ? `/api/site/admin/${adminTenantId}/testimonials`
    : '/api/site/testimonials';
  const publishPath = isAdmin
    ? `/api/site/admin/${adminTenantId}/publish`
    : '/api/site/publish';

  const showMessage = (text, type = 'info') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 4000);
  };

  const fetchData = useCallback(async () => {
    try {
      const [siteRes, testRes] = await Promise.all([
        axios.get(basePath),
        axios.get(testimonialsPath).catch(() => ({ data: { testimonials: [] } })),
      ]);
      setSettings(siteRes.data.settings || siteRes.data);
      if (siteRes.data.tenant) setTenantInfo(siteRes.data.tenant);
      if (siteRes.data.custom_domain_enabled !== undefined) setDomainEnabled(siteRes.data.custom_domain_enabled);
      setTestimonials(testRes.data.testimonials || testRes.data || []);
    } catch (err) {
      console.error('Failed to load site settings:', err);
      showMessage('Failed to load settings', 'error');
    } finally {
      setLoading(false);
    }
  }, [basePath, testimonialsPath]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateField = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const updateHours = (day, field, value) => {
    setSettings(prev => ({
      ...prev,
      business_hours: {
        ...prev.business_hours,
        [day]: { ...(prev.business_hours?.[day] || {}), [field]: value },
      },
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(basePath, settings);
      setSettings(res.data.settings || res.data);
      showMessage('Settings saved!', 'success');
    } catch (err) {
      showMessage(err.response?.data?.error || 'Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  };

  const verifyDomain = async () => {
    setDomainVerifying(true);
    setDomainStatus(null);
    try {
      const res = await axios.post('/api/site/verify-domain');
      if (res.data.success) {
        setDomainStatus({ verified: res.data.verified, message: res.data.message });
        if (res.data.verified) {
          setSettings(prev => ({ ...prev, custom_domain_verified: true }));
        }
      } else {
        setDomainStatus({ verified: false, message: res.data.error || 'Verification failed' });
      }
    } catch (err) {
      setDomainStatus({ verified: false, message: 'Failed to verify. Save your domain first, then try again.' });
    } finally {
      setDomainVerifying(false);
    }
  };

  const togglePublish = async () => {
    try {
      const res = await axios.post(publishPath, {
        published: !settings.is_published,
      });
      setSettings(prev => ({ ...prev, is_published: res.data.published }));
      showMessage(res.data.published ? 'Site is now live!' : 'Site unpublished', 'success');
    } catch (err) {
      showMessage('Failed to update publish status', 'error');
    }
  };

  const addTestimonial = async () => {
    if (!newTestimonial.author_name || !newTestimonial.content) {
      showMessage('Name and review text required', 'error');
      return;
    }
    try {
      const res = await axios.post(testimonialsPath, newTestimonial);
      setTestimonials(prev => [...prev, res.data.testimonial || res.data]);
      setNewTestimonial({ author_name: '', content: '', rating: 5 });
      showMessage('Testimonial added!', 'success');
    } catch (err) {
      showMessage('Failed to add testimonial', 'error');
    }
  };

  const deleteTestimonial = async (id) => {
    if (!window.confirm('Delete this testimonial?')) return;
    try {
      await axios.delete(`${testimonialsPath}/${id}`);
      setTestimonials(prev => prev.filter(t => t.id !== id));
      showMessage('Deleted', 'success');
    } catch (err) {
      showMessage('Failed to delete', 'error');
    }
  };

  if (loading) {
    return (
      <div style={s.loadingWrap}>
        <p style={{ color: '#666', marginTop: 12 }}>Loading site settings...</p>
      </div>
    );
  }

  if (!settings) {
    return <div style={s.loadingWrap}><p>Unable to load settings. Check your connection.</p></div>;
  }

  const siteUrl = settings.is_published
    ? `${window.location.origin}/site/${settings.custom_slug}`
    : null;

  const tabs = [
    { key: 'general', label: '📋 General' },
    { key: 'branding', label: '🎨 Branding' },
    { key: 'contact', label: '📍 Contact & Hours' },
    { key: 'social', label: '📱 Social' },
    { key: 'testimonials', label: '⭐ Reviews' },
    { key: 'seo', label: '🔍 SEO' },
    { key: 'sections', label: '⚙️ Sections' },
  ];

  return (
    <div className="site-settings-page" style={s.page}>
      <div style={s.container}>

        {/* HEADER */}
        <div style={s.header}>
          <div>
            <h1 style={s.title}>{isAdmin ? '🛠' : '🌐'} {isAdmin && tenantInfo ? tenantInfo.business_name + ' — ' : ''}My Website</h1>
            <p style={s.subtitle}>{isAdmin ? `Editing as admin (tenant #${adminTenantId})` : 'Customize your public landing page'}</p>
          </div>
          <div style={s.headerBtns}>
            <button onClick={togglePublish} style={{
              ...s.btn,
              background: settings.is_published ? '#dc2626' : '#16a34a',
              color: '#fff',
            }}>
              {settings.is_published ? '⏸ Unpublish' : '🚀 Publish'}
            </button>
            <button onClick={save} disabled={saving} style={{ ...s.btn, background: '#2563eb', color: '#fff' }}>
              {saving ? 'Saving...' : '💾 Save'}
            </button>
          </div>
        </div>

        {/* LIVE URL */}
        {siteUrl && (
          <div style={s.liveBar}>
            <span style={{ color: '#059669' }}>✅ Live at:</span>
            <a href={siteUrl} target="_blank" rel="noopener noreferrer" style={s.liveLink}>{siteUrl}</a>
            <button onClick={() => { navigator.clipboard.writeText(siteUrl); showMessage('Copied!', 'success'); }} style={s.copyBtn}>
              📋 Copy
            </button>
          </div>
        )}

        {/* MESSAGE */}
        {message.text && (
          <div style={{
            ...s.msg,
            background: message.type === 'error' ? '#fef2f2' : message.type === 'success' ? '#ecfdf5' : '#f0f9ff',
            borderColor: message.type === 'error' ? '#fecaca' : message.type === 'success' ? '#a7f3d0' : '#bae6fd',
          }}>
            {message.type === 'success' ? '✅' : message.type === 'error' ? '❌' : 'ℹ️'} {message.text}
          </div>
        )}

        {/* TABS */}
        <div style={s.tabBar}>
          {tabs.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
              ...s.tab,
              ...(activeTab === tab.key ? s.tabActive : {}),
            }}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* CONTENT */}
        <div style={s.card}>

          {/* GENERAL */}
          {activeTab === 'general' && (
            <div>
              <h3 style={s.h3}>Basic Information</h3>
              <Label text="URL Slug" hint={`Your page: /site/${settings.custom_slug || '...'}`}>
                <input style={s.input} value={settings.custom_slug || ''}
                  onChange={e => updateField('custom_slug', e.target.value)} placeholder="angel-massage-spa" />
              </Label>

              {/* CUSTOM DOMAIN SECTION */}
              {domainEnabled ? (
                <div style={{ margin: '16px 0 24px', padding: '20px', background: '#1a1c2e', borderRadius: '12px', border: '1px solid #1e1e35' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <div>
                      <h4 style={{ color: '#fff', fontSize: '15px', fontWeight: '700', margin: 0 }}>🌐 Custom Domain</h4>
                      <p style={{ color: '#888', fontSize: '12px', margin: '4px 0 0' }}>Connect your own domain to this website</p>
                    </div>
                    {settings.custom_domain_verified && (
                      <span style={{ fontSize: '11px', padding: '4px 12px', borderRadius: '20px', background: '#00b89420', color: '#00b894', fontWeight: '700' }}>✓ Connected</span>
                    )}
                  </div>

                  <Label text="Your Domain" hint="e.g. www.yourbusiness.com">
                    <input style={s.input} value={settings.custom_domain || ''}
                      onChange={e => updateField('custom_domain', e.target.value)}
                      placeholder="www.yourbusiness.com" />
                  </Label>

                  {settings.custom_domain && (
                    <div style={{ marginTop: '12px' }}>
                      <div style={{ padding: '14px', background: '#12121f', borderRadius: '10px', border: '1px solid #1e1e35', marginBottom: '12px' }}>
                        <p style={{ color: '#ffab40', fontSize: '12px', fontWeight: '700', margin: '0 0 8px' }}>📋 DNS Setup Instructions</p>
                        <p style={{ color: '#aaa', fontSize: '12px', margin: '0 0 8px', lineHeight: '1.6' }}>
                          Log into your domain provider (GoDaddy, Namecheap, Google Domains, etc.) and add this DNS record:
                        </p>
                        <div style={{ display: 'grid', gridTemplateColumns: '70px 1fr', gap: '4px 12px', fontSize: '12px' }}>
                          <span style={{ color: '#666', fontWeight: '600' }}>Type:</span><span style={{ color: '#fff', fontFamily: 'monospace' }}>CNAME</span>
                          <span style={{ color: '#666', fontWeight: '600' }}>Name:</span><span style={{ color: '#fff', fontFamily: 'monospace' }}>{(settings.custom_domain || '').startsWith('www.') ? 'www' : '@'}</span>
                          <span style={{ color: '#666', fontWeight: '600' }}>Value:</span><span style={{ color: '#00b894', fontFamily: 'monospace' }}>cname.vercel-dns.com</span>
                        </div>
                        <p style={{ color: '#666', fontSize: '11px', margin: '10px 0 0' }}>DNS changes can take up to 48 hours to propagate. Save your settings first, then verify.</p>
                      </div>

                      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <button onClick={verifyDomain} disabled={domainVerifying}
                          style={{ padding: '10px 20px', background: domainVerifying ? '#333' : '#0984e3', border: 'none', borderRadius: '10px', color: '#fff', fontSize: '13px', fontWeight: '600', cursor: domainVerifying ? 'default' : 'pointer' }}>
                          {domainVerifying ? 'Checking...' : '🔍 Verify DNS'}
                        </button>
                        {domainStatus && (
                          <span style={{ fontSize: '12px', color: domainStatus.verified ? '#00b894' : '#f59e0b', fontWeight: '600' }}>
                            {domainStatus.verified ? '✓ ' : '⏳ '}{domainStatus.message}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : !isAdmin && (
                <div style={{ margin: '16px 0 24px', padding: '20px', background: 'linear-gradient(135deg, #0984e308, #6c5ce708)', borderRadius: '12px', border: '1px dashed #1e1e45' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                    <div>
                      <h4 style={{ color: '#fff', fontSize: '15px', fontWeight: '700', margin: '0 0 4px' }}>🌐 Want your own domain?</h4>
                      <p style={{ color: '#888', fontSize: '13px', margin: 0 }}>
                        Connect a custom domain like <span style={{ color: '#74b9ff' }}>www.yourbusiness.com</span> for just <span style={{ color: '#ffab40', fontWeight: '700' }}>$29/mo</span>
                      </p>
                    </div>
                    <a href="mailto:support@ivisionmarketing.com?subject=Custom%20Domain%20Add-on&body=I'd%20like%20to%20add%20a%20custom%20domain%20to%20my%20website."
                      style={{ padding: '10px 20px', background: 'linear-gradient(135deg,#ff6b2b,#ffab40)', borderRadius: '10px', color: '#fff', fontSize: '13px', fontWeight: '700', textDecoration: 'none', whiteSpace: 'nowrap' }}>
                      Upgrade →
                    </a>
                  </div>
                </div>
              )}
              <Label text="Tagline" hint="Shown in hero, SEO, and throughout the page">
                <input style={s.input} value={settings.tagline || ''}
                  onChange={e => updateField('tagline', e.target.value)}
                  placeholder="Las Vegas Premier Massage & Wellness" maxLength={200} />
              </Label>
              <Label text="About Your Business" hint="Shown in the About section on your site">
                <textarea style={{ ...s.input, minHeight: 120 }} value={settings.about_text || ''}
                  onChange={e => updateField('about_text', e.target.value)}
                  placeholder="We've been serving the Las Vegas community since..." />
              </Label>

              <h3 style={{ ...s.h3, marginTop: 32 }}>Logo</h3>
              <p style={{ fontSize: 13, color: '#64748b', margin: '-8px 0 12px' }}>Appears in your navigation bar, footer, and About section. Square or horizontal logos work best.</p>
              <ImageUploader
                value={settings.logo_url || ''}
                onChange={url => updateField('logo_url', url)}
                label="Upload Logo"
                maxWidth={400}
                maxHeight={400}
                maxSizeKB={300}
              />

              <h3 style={{ ...s.h3, marginTop: 32 }}>Hero Image</h3>
              <p style={{ fontSize: 13, color: '#64748b', margin: '-8px 0 12px' }}>Large background/banner image shown at the top of your site. Landscape photos work best (1200×600+).</p>
              <ImageUploader
                value={settings.hero_image_url || ''}
                onChange={url => updateField('hero_image_url', url)}
                label="Upload Hero Image"
                maxWidth={1400}
                maxHeight={800}
                maxSizeKB={500}
              />

              <h3 style={{ ...s.h3, marginTop: 32 }}>Gallery Images</h3>
              <p style={{ fontSize: 13, color: '#64748b', margin: '-8px 0 12px' }}>Show off your space, work, and team. These appear in the Gallery section (enable it in Sections tab).</p>
              <GalleryUploader
                images={settings.gallery_images || []}
                onChange={imgs => updateField('gallery_images', imgs)}
              />
            </div>
          )}

          {/* BRANDING */}
          {activeTab === 'branding' && (
            <div>
              <h3 style={s.h3}>Colors & Style</h3>
              <div style={s.grid2}>
                <Label text="Primary Color">
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <input type="color" value={settings.primary_color || '#1a5f2a'}
                      onChange={e => updateField('primary_color', e.target.value)}
                      style={s.colorPicker} />
                    <input style={{ ...s.input, flex: 1 }} value={settings.primary_color || ''}
                      onChange={e => updateField('primary_color', e.target.value)} placeholder="#1a5f2a" />
                  </div>
                </Label>
                <Label text="Accent Color">
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <input type="color" value={settings.secondary_color || '#d4a853'}
                      onChange={e => updateField('secondary_color', e.target.value)}
                      style={s.colorPicker} />
                    <input style={{ ...s.input, flex: 1 }} value={settings.secondary_color || ''}
                      onChange={e => updateField('secondary_color', e.target.value)} placeholder="#d4a853" />
                  </div>
                </Label>
              </div>
              <Label text="Quick Presets" hint="Click to apply a color scheme">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {[
                    { name: 'Forest', p: '#1a5f2a', a: '#d4a853' },
                    { name: 'Ocean', p: '#1e40af', a: '#f59e0b' },
                    { name: 'Rose', p: '#9f1239', a: '#d4a853' },
                    { name: 'Purple', p: '#5b21b6', a: '#f472b6' },
                    { name: 'Midnight', p: '#1e293b', a: '#38bdf8' },
                    { name: 'Earth', p: '#92400e', a: '#a3e635' },
                    { name: 'Coral', p: '#be123c', a: '#2dd4bf' },
                    { name: 'Sage', p: '#4d7c0f', a: '#fbbf24' },
                  ].map(preset => (
                    <button key={preset.name} onClick={() => {
                      updateField('primary_color', preset.p);
                      updateField('secondary_color', preset.a);
                    }} style={{
                      padding: '6px 14px', borderRadius: 8, border: '1px solid #e2e8f0',
                      background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 500,
                      display: 'flex', alignItems: 'center', gap: 8,
                    }}>
                      <span style={{ width: 14, height: 14, borderRadius: 4, background: preset.p, display: 'inline-block' }}></span>
                      <span style={{ width: 14, height: 14, borderRadius: 4, background: preset.a, display: 'inline-block' }}></span>
                      {preset.name}
                    </button>
                  ))}
                </div>
              </Label>
              <Label text="Font Style">
                <select style={s.input} value={settings.font_choice || 'modern'}
                  onChange={e => updateField('font_choice', e.target.value)}>
                  <option value="modern">Modern (DM Sans)</option>
                  <option value="elegant">Elegant (Cormorant Garamond)</option>
                  <option value="bold">Bold (Outfit)</option>
                </select>
              </Label>
              <Label text="Template">
                <select style={s.input} value={settings.template || 'spa-classic'}
                  onChange={e => updateField('template', e.target.value)}>
                  <option value="spa-classic">Spa Classic — warm, elegant, nature tones</option>
                  <option value="modern-minimal">Modern Minimal — clean, bright, sharp</option>
                  <option value="bold-dark">Bold Dark — dramatic, premium feel</option>
                </select>
              </Label>
              <div style={{
                marginTop: 20, padding: 20, borderRadius: 12,
                background: settings.primary_color || '#1a5f2a',
                color: '#fff',
              }}>
                <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>Preview</div>
                <div style={{ opacity: 0.8, fontSize: 14, marginBottom: 12 }}>This is how your primary color looks</div>
                <span style={{
                  display: 'inline-block', padding: '8px 20px', borderRadius: 100,
                  background: settings.secondary_color || '#d4a853',
                  color: '#fff', fontWeight: 600, fontSize: 13,
                }}>Accent Button</span>
              </div>
            </div>
          )}

          {/* CONTACT & HOURS */}
          {activeTab === 'contact' && (
            <div>
              <h3 style={s.h3}>Contact Information</h3>
              <div style={s.grid2}>
                <Label text="Phone">
                  <input style={s.input} value={settings.display_phone || ''}
                    onChange={e => updateField('display_phone', e.target.value)} placeholder="(702) 555-1234" />
                </Label>
                <Label text="Email">
                  <input style={s.input} value={settings.display_email || ''}
                    onChange={e => updateField('display_email', e.target.value)} placeholder="info@yourspa.com" />
                </Label>
              </div>
              <Label text="Street Address">
                <input style={s.input} value={settings.display_address || ''}
                  onChange={e => updateField('display_address', e.target.value)} placeholder="1234 Main St" />
              </Label>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 16 }}>
                <Label text="City">
                  <input style={s.input} value={settings.city || ''}
                    onChange={e => updateField('city', e.target.value)} placeholder="Las Vegas" />
                </Label>
                <Label text="State">
                  <input style={s.input} value={settings.state || ''}
                    onChange={e => updateField('state', e.target.value)} placeholder="NV" />
                </Label>
                <Label text="ZIP">
                  <input style={s.input} value={settings.zip || ''}
                    onChange={e => updateField('zip', e.target.value)} placeholder="89101" />
                </Label>
              </div>
              <Label text="Google Maps Embed" hint="Paste the iframe embed code from Google Maps">
                <textarea style={{ ...s.input, minHeight: 70, fontFamily: 'monospace', fontSize: 12 }}
                  value={settings.google_maps_embed || ''}
                  onChange={e => updateField('google_maps_embed', e.target.value)}
                  placeholder='Paste Google Maps embed code here' />
              </Label>
              <h3 style={{ ...s.h3, marginTop: 28 }}>Business Hours</h3>
              {DAYS.map(day => {
                const h = settings.business_hours?.[day] || {};
                const isClosed = h.closed === true || (!h.open && !h.close);
                return (
                  <div key={day} style={{ ...s.hoursRow, opacity: isClosed ? 0.5 : 1 }}>
                    <span style={s.dayLabel}>{day.charAt(0).toUpperCase() + day.slice(1)}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div onClick={() => {
                        setSettings(prev => ({
                          ...prev,
                          business_hours: {
                            ...prev.business_hours,
                            [day]: isClosed
                              ? { open: h.open || '09:00', close: h.close || '17:00', closed: false }
                              : { ...h, closed: true },
                          },
                        }));
                      }} style={{
                        width: 40, height: 22, borderRadius: 11, cursor: 'pointer',
                        background: isClosed ? '#d1d5db' : '#10b981', position: 'relative', transition: 'all 0.2s',
                      }}>
                        <div style={{
                          width: 18, height: 18, borderRadius: 9, background: '#fff',
                          position: 'absolute', top: 2, left: isClosed ? 2 : 20, transition: 'left 0.2s',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                        }} />
                      </div>
                      <span style={{ fontSize: 12, color: isClosed ? '#999' : '#059669', fontWeight: 600, minWidth: 45 }}>
                        {isClosed ? 'Closed' : 'Open'}
                      </span>
                    </div>
                    {!isClosed ? (
                      <>
                        <select style={s.timeSelect} value={h.open || '09:00'}
                          onChange={e => updateHours(day, 'open', e.target.value)}>
                          {TIME_OPTIONS.map(t => <option key={t} value={t}>{formatTime(t)}</option>)}
                        </select>
                        <span style={{ color: '#999', fontSize: 13 }}>to</span>
                        <select style={s.timeSelect} value={h.close || '17:00'}
                          onChange={e => updateHours(day, 'close', e.target.value)}>
                          {TIME_OPTIONS.map(t => <option key={t} value={t}>{formatTime(t)}</option>)}
                        </select>
                      </>
                    ) : (
                      <span style={{ gridColumn: 'span 3', color: '#999', fontSize: 13, fontStyle: 'italic' }}>Closed</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* SOCIAL */}
          {activeTab === 'social' && (
            <div>
              <h3 style={s.h3}>Social Media Links</h3>
              {[
                { field: 'facebook_url', label: '📘 Facebook', ph: 'https://facebook.com/yourbusiness' },
                { field: 'instagram_url', label: '📸 Instagram', ph: 'https://instagram.com/yourbusiness' },
                { field: 'yelp_url', label: '⭐ Yelp', ph: 'https://yelp.com/biz/yourbusiness' },
                { field: 'google_url', label: '🔍 Google Business', ph: 'https://g.page/yourbusiness' },
                { field: 'tiktok_url', label: '🎵 TikTok', ph: 'https://tiktok.com/@yourbusiness' },
              ].map(item => (
                <Label key={item.field} text={item.label}>
                  <input style={s.input} value={settings[item.field] || ''}
                    onChange={e => updateField(item.field, e.target.value)} placeholder={item.ph} />
                </Label>
              ))}
            </div>
          )}

          {/* TESTIMONIALS */}
          {activeTab === 'testimonials' && (
            <div>
              <h3 style={s.h3}>Manage Reviews</h3>
              <p style={{ ...s.hint, marginBottom: 16 }}>Add client reviews that appear on your landing page.</p>
              <div style={s.addForm}>
                <div style={s.grid2}>
                  <Label text="Client Name">
                    <input style={s.input} value={newTestimonial.author_name}
                      onChange={e => setNewTestimonial(p => ({ ...p, author_name: e.target.value }))}
                      placeholder="Jane D." />
                  </Label>
                  <Label text="Rating">
                    <select style={s.input} value={newTestimonial.rating}
                      onChange={e => setNewTestimonial(p => ({ ...p, rating: Number(e.target.value) }))}>
                      {[5, 4, 3, 2, 1].map(r => <option key={r} value={r}>{'★'.repeat(r)} ({r})</option>)}
                    </select>
                  </Label>
                </div>
                <Label text="Review Text">
                  <textarea style={{ ...s.input, minHeight: 80 }} value={newTestimonial.content}
                    onChange={e => setNewTestimonial(p => ({ ...p, content: e.target.value }))}
                    placeholder="Best massage I've ever had! The staff was so professional..." />
                </Label>
                <button onClick={addTestimonial} style={{ ...s.btn, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                  + Add Review
                </button>
              </div>
              <div style={{ marginTop: 20 }}>
                {testimonials.length === 0 && <p style={{ color: '#999', fontStyle: 'italic', textAlign: 'center', padding: 24 }}>No reviews yet — add your first one above</p>}
                {testimonials.map(t => (
                  <div key={t.id} style={s.testimonialItem}>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: '#f59e0b', marginBottom: 4 }}>{'★'.repeat(t.rating)}{'☆'.repeat(5 - t.rating)}</div>
                      <div style={{ fontStyle: 'italic', marginBottom: 6, color: '#374151' }}>"{t.content}"</div>
                      <div style={{ fontSize: 13, color: '#6b7280' }}>— {t.author_name}</div>
                    </div>
                    <button onClick={() => deleteTestimonial(t.id)} style={s.deleteBtn}>🗑</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* SEO */}
          {activeTab === 'seo' && (
            <div>
              <h3 style={s.h3}>Search Engine Optimization</h3>
              <Label text="Page Title" hint="Browser tab + Google results title">
                <input style={s.input} value={settings.meta_title || ''}
                  onChange={e => updateField('meta_title', e.target.value)}
                  placeholder="Angel Massage & Spa - Las Vegas Premier Wellness" maxLength={200} />
              </Label>
              <Label text="Meta Description" hint="Shows below title in Google (aim for 150-160 chars)">
                <textarea style={{ ...s.input, minHeight: 80 }} value={settings.meta_description || ''}
                  onChange={e => updateField('meta_description', e.target.value)}
                  placeholder="Experience the best massage therapy in Las Vegas. Book online today." maxLength={500} />
                <span style={{ fontSize: 12, color: '#9ca3af', float: 'right' }}>
                  {(settings.meta_description || '').length}/500
                </span>
              </Label>
              <div style={{ marginTop: 24, padding: 20, background: '#f8fafc', borderRadius: 12, border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 8 }}>Google Preview</div>
                <div style={{ color: '#1a0dab', fontSize: 18, marginBottom: 2 }}>
                  {settings.meta_title || 'Your Business - Book Online'}
                </div>
                <div style={{ color: '#006621', fontSize: 13, marginBottom: 4 }}>
                  {settings.custom_domain && settings.custom_domain_verified
                    ? settings.custom_domain
                    : 'ivisionmarketing.com/site/' + (settings.custom_slug || '...')}
                </div>
                <div style={{ color: '#545454', fontSize: 13 }}>
                  {settings.meta_description || 'Add a meta description to control what shows here.'}
                </div>
              </div>
            </div>
          )}

          {/* SECTIONS */}
          {activeTab === 'sections' && (
            <div>
              <h3 style={s.h3}>Show/Hide Sections</h3>
              <p style={{ ...s.hint, marginBottom: 20 }}>Toggle which sections appear on your landing page.</p>
              {[
                { field: 'show_services', label: '💆 Services Menu', desc: 'List of your services with prices' },
                { field: 'show_staff', label: '👥 Team Members', desc: 'Staff names and roles' },
                { field: 'show_reviews', label: '⭐ Testimonials', desc: 'Client reviews and ratings' },
                { field: 'show_gallery', label: '📸 Photo Gallery', desc: 'Images of your space' },
                { field: 'show_hours', label: '🕐 Business Hours', desc: 'Daily open/close times' },
                { field: 'show_map', label: '📍 Google Map', desc: 'Embedded map of your location' },
                { field: 'show_booking_button', label: '📅 Book Now Button', desc: 'CTA buttons linking to online booking' },
              ].map(item => (
                <label key={item.field} style={s.toggleRow}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{item.label}</div>
                    <div style={{ fontSize: 12, color: '#9ca3af' }}>{item.desc}</div>
                  </div>
                  <div style={s.toggleSwitch} onClick={() => updateField(item.field, !settings[item.field])}>
                    <div style={{
                      ...s.toggleKnob,
                      transform: settings[item.field] !== false ? 'translateX(20px)' : 'translateX(0)',
                      background: settings[item.field] !== false ? '#16a34a' : '#d1d5db',
                    }}></div>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Label({ text, hint, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 6, color: '#374151' }}>{text}</label>
      {children}
      {hint && <span style={{ display: 'block', fontSize: 12, color: '#9ca3af', marginTop: 4 }}>{hint}</span>}
    </div>
  );
}

const s = {
  page: { background: '#f8fafc', minHeight: '100vh' },
  container: { maxWidth: 880, margin: '0 auto', padding: '24px 16px 60px' },
  loadingWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16, marginBottom: 20 },
  title: { margin: 0, fontSize: 22, fontWeight: 700, color: '#0f172a' },
  subtitle: { margin: '4px 0 0', fontSize: 14, color: '#64748b' },
  headerBtns: { display: 'flex', gap: 8 },
  btn: { padding: '9px 20px', borderRadius: 8, border: 'none', fontWeight: 600, fontSize: 13, cursor: 'pointer' },
  liveBar: {
    background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 8,
    padding: '10px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 14,
  },
  liveLink: { color: '#059669', fontWeight: 600, textDecoration: 'underline' },
  copyBtn: { padding: '4px 12px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 12 },
  msg: { padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 14, border: '1px solid' },
  tabBar: { display: 'flex', gap: 4, marginBottom: 20, overflowX: 'auto', borderBottom: '2px solid #e5e7eb', paddingBottom: 0 },
  tab: {
    padding: '8px 14px', borderRadius: '8px 8px 0 0', border: 'none',
    background: 'transparent', cursor: 'pointer', fontWeight: 500,
    fontSize: 13, color: '#6b7280', whiteSpace: 'nowrap', marginBottom: -2,
  },
  tabActive: { background: '#fff', color: '#0f172a', borderBottom: '2px solid #2563eb' },
  card: { background: '#fff', borderRadius: 12, padding: 28, border: '1px solid #e5e7eb' },
  h3: { fontSize: 16, fontWeight: 700, marginBottom: 20, color: '#0f172a' },
  input: {
    width: '100%', padding: '9px 12px', borderRadius: 8,
    border: '1px solid #d1d5db', fontSize: 14, fontFamily: 'inherit',
    outline: 'none', boxSizing: 'border-box',
  },
  hint: { fontSize: 12, color: '#9ca3af' },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
  colorPicker: { width: 44, height: 38, border: 'none', borderRadius: 8, cursor: 'pointer', padding: 0 },
  hoursRow: {
    display: 'grid', gridTemplateColumns: '100px 110px 1fr auto 1fr', gap: 10, alignItems: 'center',
    padding: '10px 0', borderBottom: '1px solid #f3f4f6',
  },
  dayLabel: { fontWeight: 600, fontSize: 14, textTransform: 'capitalize' },
  timeInput: { padding: '7px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14 },
  timeSelect: { padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, background: '#fff', cursor: 'pointer' },
  addForm: { background: '#f9fafb', borderRadius: 10, padding: 20, border: '1px dashed #d1d5db' },
  testimonialItem: {
    display: 'flex', alignItems: 'flex-start', gap: 12,
    padding: '16px 0', borderBottom: '1px solid #f3f4f6',
  },
  deleteBtn: {
    padding: '6px 10px', borderRadius: 6, border: '1px solid #fecaca',
    background: '#fef2f2', cursor: 'pointer', fontSize: 14,
  },
  toggleRow: {
    display: 'flex', alignItems: 'center', gap: 16, padding: '14px 0',
    borderBottom: '1px solid #f3f4f6', cursor: 'pointer',
  },
  toggleSwitch: {
    width: 44, height: 24, borderRadius: 12, background: '#e5e7eb',
    position: 'relative', cursor: 'pointer', transition: 'background 0.2s',
  },
  toggleKnob: {
    width: 20, height: 20, borderRadius: 10,
    position: 'absolute', top: 2, left: 2,
    transition: 'all 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
};
